/**
 * Basil — The Heavy-Duty Tinkerer.
 * Striped tabby with a brass-and-leather pneumatic mobility harness.
 * Extends PlayerEntity with heavier physics and ability initialization.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { PLAYER, COLORS } from "../../config/GameConfig";
import { InputSource } from "../../systems/InputSource";
import { AbilityHolder } from "../../systems/AbilitySystem";
import { PneumaticAnchor } from "../../abilities/basil/PneumaticAnchor";
import { basilAnimClips } from "./catAnimClips";
import { PlayerEntity, PlayerConfig } from "./PlayerEntity";
import { applyHarnessBrassEmissive, applyHarnessAlbedoFallback } from "./characterAccents";
import {
  advanceScratchOverlay,
  computeScratchOverlay,
  createScratchOverlayPose,
  createScratchOverlayState,
  wireScratchOverlayEvents,
} from "./scratchOverlay";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import type { HarnessState } from "../../config/GameConfig";

/** Basil is heavier and slightly wider than Minna. */
const BASIL_MASS = 1.5;
const BASIL_RADIUS = PLAYER.CAPSULE_RADIUS * 1.15;
export const BASIL_CAPSULE_HEIGHT = PLAYER.CAPSULE_HEIGHT * 1.05;

const BASIL_MODEL_SCALE = 0.82;

const BASIL_CONFIG: PlayerConfig = {
  name: "basil",
  // basil_harnessed.glb (merged body + brass-and-leather harness parts)
  // rigged and animated by tools/blender/rig_cat.py: idle/walk/run/jump/
  // fall/land/crouch clips, harness parts riding hips and spine. The
  // CatAnimator in PlayerEntity drives the clips.
  modelPath: "/assets/models/basil_anim.glb",
  modelScale: BASIL_MODEL_SCALE,
  animClips: basilAnimClips(BASIL_MODEL_SCALE),
  mass: BASIL_MASS,
  capsuleRadius: BASIL_RADIUS,
  capsuleHeight: BASIL_CAPSULE_HEIGHT,
  airAccelerationScale: 0.85,
  // Task 8: Basil feels heavier via a modest jumpImpulse reduction and
  // higher ground deceleration (more ground-stick, harder stops).
  // jumpImpulse=8.2 still clears every required platform gap in L0-L3;
  // see arc-reachability tests in cat-navigator.spec.ts.
  //
  // IMPORTANT — OverclockedLeap consistency: OverclockedLeap.superJump()
  // reads PLAYER.JUMP_IMPULSE (9) directly, NOT this per-cat value.
  // NavigationSystem derives ovLeapVyInit via mp.jumpImpulse * 2.5, which
  // would give 8.2*2.5=20.5 if left uncorrected. To keep the navigator's
  // arc prediction matching the actual OvLeap impulse (22.5), NavigationSystem
  // uses PLAYER.JUMP_IMPULSE for the OvLeap term — see the fix there.
  // 2026-09-26: 8.2 → 8.7. At 8.2 Basil's full jump gained 2.21u, and the
  // levels' standard 2.0u step (L0 p3→p4, and six more in L1/L3) left 0.21u
  // of margin: a running jump succeeded 4/30 times and first-timers needed
  // ~9 tries (tools/playtest hops.mjs). 8.7 gains ~2.49u (≈25% margin) and
  // still can't reach the ≥3u ledges that need Overclocked Leap.
  jumpImpulse: 8.7,
  deceleration: 48,          // 35 → 48: more ground-stick on stops
  lightColor: Color3.FromHexString(COLORS.COPPER),
  lightSpecular: Color3.FromHexString(COLORS.BASIL_HARNESS),
  lightIntensity: 0.8,
  lightRange: 4,
  lightOffset: new Vector3(0, 0, 0.2),
  // Copper tabby fallback when the GLB fur material lacks an albedo texture.
  furTint: new Color3(0.55, 0.3, 0.14),
  footstepStrideDistance: 0.9,
};

export class Basil extends PlayerEntity {
  /**
   * Reference to Basil's canonical ability holder, owned by
   * `setupBaseLevel` and injected via {@link setAbilityContext} after
   * the scene has constructed the abilities against this entity's mesh
   * and physics body.
   *
   * Historical context: Basil used to construct its own holder + full
   * ability set, parallel to BaseLevel's holder. That produced two
   * instances of each ability per scene — activation and update ticked
   * different instances, so {@link OverclockedLeap.chargeTimer} never
   * reached 0.35 s on the instance that `ability:activated` emitted
   * from, and the `superJump()` impulse never fired. See
   * `docs/qa/analysis/startatpuzzle-audit-2026-04-18.md` §2.4.
   */
  private abilityHolder: AbilityHolder | null = null;

  /** Canonical Pneumatic Anchor, used to mirror isActive → movementBlocked. */
  private pneumaticAnchor: PneumaticAnchor | null = null;

  /** Live harness state mirrored from HarnessWoundSystem events. */
  private harnessState: HarnessState = "intact";

  /**
   * Venting-state steam particle system. Created lazily the first time
   * Basil enters Venting or Limping and disposed when he's healed back
   * to Cracked/Intact. Visual spec §5.
   */
  private harnessSteam: ParticleSystem | null = null;

  /** Warm amber/red glow that reads "harness is hissing" at distance. */
  private harnessGlowLight: PointLight | null = null;

  private harnessUnsub: (() => void) | null = null;

  // ── Turn-Table Touch "DJ scratch" pose overlay (Q-032) ──────────────
  // Root-level only: basil_harnessed.glb is a merged body with no limb
  // nodes, so the limb rig no-ops and the scratch reads through modelRoot
  // squash/lean/flick. State + pose are allocated once (zero per-frame
  // allocations); the math lives in scratchOverlay.ts as pure functions.
  private tttOverlay = createScratchOverlayState();
  private tttPose = createScratchOverlayPose();
  /** Mirrored from the injected holder each applyInput tick. */
  private tttActive = false;
  private tttUnsubs: Array<() => void> = [];

  constructor(scene: Scene, spawnPosition: Vector3) {
    super(scene, spawnPosition, BASIL_CONFIG);

    // Harness state listener — wires the visual signature described in
    // spec §5. The wound system itself is owned by BaseLevel; Basil
    // only mirrors the public event stream.
    this.harnessUnsub = gameEvents.on(GameEvents.HARNESS_STATE_CHANGED, (data) => {
      this.harnessState = data.next;
      this.applyHarnessVisualState();
    });

    // Rhythm feedback listeners (mirror the HARNESS_STATE_CHANGED
    // pattern): each successful TTT hit fires a snappy paw-flick toward
    // the dial; a miss triggers a brief rattle. Purely visual — the
    // overlay only ever writes modelRoot transforms. The wiring lives in
    // scratchOverlay.ts so unit tests can exercise it without physics.
    this.tttUnsubs.push(wireScratchOverlayEvents(this.tttOverlay));
  }

  /**
   * Inject the canonical Basil ability holder and PneumaticAnchor
   * instance. Must be called from `setupBaseLevel` after abilities are
   * constructed and before the first `applyInput` tick.
   */
  setAbilityContext(holder: AbilityHolder, pneumaticAnchor: PneumaticAnchor): void {
    this.abilityHolder = holder;
    this.pneumaticAnchor = pneumaticAnchor;
  }

  override applyInput(input: InputSource, deltaTime: number): void {
    // Ability cooldown + active-update ticking is owned by
    // `setupBaseLevel`'s game loop (`basilAbilities.update(dt)`).
    // Do NOT tick here — doing so against a private holder is what
    // produced the duplicate-instance bug this class was refactored to
    // fix.

    // Overclocked Leap activation via the generic "ability" button (P1
    // "f" / P2 "/" / gamepad face button). The digit-bound path (`4`)
    // goes through AbilityInputMapper; this is the gamepad / shortcut
    // path. Both routes now hit the SAME holder instance.
    if (this.abilityHolder && input.justPressed("ability") && this.isGrounded) {
      this.abilityHolder.activateAbility('overclockedLeap');
    }

    // Pneumatic Anchor pins Basil in place. Mirror the ability's
    // isActive into the PlayerEntity.movementBlocked flag so
    // horizontal movement attempts trigger the denied-input rattle
    // feedback instead of silently being swallowed.
    this.movementBlocked = this.pneumaticAnchor?.isActive ?? false;

    // Mirror Turn-Table Touch active state for the scratch pose overlay
    // (same holder-poll pattern as the anchor mirror above — the holder
    // is the canonical instance set, see the class-level comment).
    this.tttActive =
      this.abilityHolder?.getAbility("turnTableTouch")?.isActive ?? false;

    super.applyInput(input, deltaTime);
  }

  /**
   * Q-032 "DJ scratch" pose while Turn-Table Touch is active: crouch over
   * the deck, lean toward the dial with a paw-working wobble, flick on
   * every rhythm hit, rattle on a miss. Runs AFTER the base pose
   * composition (overwrite semantics upstream), composes with += / *=,
   * and early-outs to ~zero cost while TTT is inactive. Only modelRoot
   * transforms are written — never the physics capsule.
   */
  protected override applyAbilityPoseOverlay(deltaTime: number): void {
    const root = this.modelRoot;
    if (!root) return;
    if (!advanceScratchOverlay(this.tttOverlay, deltaTime, this.tttActive)) {
      return;
    }
    // Walking dampens the pose so the scratch never fights the gait.
    const calm = 1 - this.smoothedMoveAmount;
    const pose = computeScratchOverlay(
      this.tttOverlay,
      this.facingDirection,
      calm,
      this.tttPose,
    );
    root.position.x += pose.dx;
    root.rotation.z += pose.dRotZ;
    root.scaling.x *= pose.scaleXMul;
    root.scaling.y *= pose.scaleYMul;
  }

  protected override onModelLoaded(): void {
    // Q-038: the brass harness read faintly at gameplay zoom — give the
    // cloned Harness_Brass material a dim self-light so the copper kit
    // reads at distance. Material is a per-instance clone (AssetManager
    // cloneMaterials default), so this never leaks into the cached template.
    applyHarnessAlbedoFallback(this.modelMeshes);
    applyHarnessBrassEmissive(this.modelMeshes);
  }

  override dispose(): void {
    this.harnessUnsub?.();
    this.harnessUnsub = null;
    for (const unsub of this.tttUnsubs) {
      unsub();
    }
    this.tttUnsubs.length = 0;
    this.harnessSteam?.stop();
    this.harnessSteam?.dispose();
    this.harnessSteam = null;
    this.harnessGlowLight?.dispose();
    this.harnessGlowLight = null;
    super.dispose();
  }

  /**
   * Apply harness state visuals per spec §5. Only the three
   * "not-intact" states get a signature; Intact is clean.
   */
  private applyHarnessVisualState(): void {
    const s = this.harnessState;
    const needsSteam = s === "venting" || s === "limping";

    // Dispose steam + light when returning to Intact/Cracked.
    if (!needsSteam) {
      if (this.harnessSteam) {
        this.harnessSteam.stop();
        this.harnessSteam.dispose();
        this.harnessSteam = null;
      }
      if (this.harnessGlowLight) {
        this.harnessGlowLight.dispose();
        this.harnessGlowLight = null;
      }
    } else {
      this.ensureHarnessSteam(s);
    }
  }

  private ensureHarnessSteam(state: HarnessState): void {
    const scene = this.scene;
    if (!this.harnessSteam) {
      const ps = new ParticleSystem("basilHarnessSteam", 64, scene);
      ps.emitter = this.mesh;
      ps.minEmitBox = new Vector3(-0.25, -0.1, -0.15);
      ps.maxEmitBox = new Vector3(0.25, 0.3, 0.15);
      ps.color1 = new Color4(0.85, 0.55, 0.25, 0.7);
      ps.color2 = new Color4(0.55, 0.35, 0.25, 0.5);
      ps.colorDead = new Color4(0.1, 0.1, 0.1, 0);
      ps.minSize = 0.1;
      ps.maxSize = 0.3;
      ps.minLifeTime = 0.6;
      ps.maxLifeTime = 1.4;
      ps.emitRate = 0;
      ps.direction1 = new Vector3(-0.4, 1.0, -0.4);
      ps.direction2 = new Vector3(0.4, 1.8, 0.4);
      ps.gravity = new Vector3(0, 0.8, 0);
      ps.blendMode = ParticleSystem.BLENDMODE_ADD;
      this.harnessSteam = ps;
      ps.start();
    }
    // Scale rate + add a deep-red glow when Limping.
    if (state === "venting") {
      this.harnessSteam.emitRate = 24;
      this.harnessSteam.color1 = new Color4(0.85, 0.55, 0.25, 0.7);
    } else {
      // Limping — heavier smoke, darker tint.
      this.harnessSteam.emitRate = 42;
      this.harnessSteam.color1 = new Color4(0.6, 0.25, 0.15, 0.85);
    }

    if (!this.harnessGlowLight) {
      const light = new PointLight(
        "basilHarnessGlow",
        Vector3.Zero(),
        scene,
      );
      light.parent = this.mesh;
      light.position = new Vector3(0, 0.1, 0.18);
      light.diffuse = Color3.FromHexString(COLORS.COPPER);
      light.specular = Color3.FromHexString("#cc4422");
      light.intensity = 0.8;
      light.range = 1.5;
      this.harnessGlowLight = light;
    }
    if (state === "limping" && this.harnessGlowLight) {
      this.harnessGlowLight.intensity = 1.6;
      this.harnessGlowLight.range = 2.5;
      this.harnessGlowLight.diffuse = Color3.FromHexString("#cc4422");
    }
  }
}
